import { useEffect, useState } from 'react';
import MobileMenu from '../../components/MobileMenu';
import Hero from './components/Hero';
import Features from './components/Features';
import Portfolio from './components/Portfolio';
import Pricing from './components/Pricing';
import About from './components/About';
import Footer from './components/Footer';

export default function Home() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(prefersDark);
  }, []);

  // Nav: subtle background once user has scrolled past the hero
  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className={`relative ${isDarkMode ? 'bg-[#050505]' : 'bg-white'}`}>
      {/* Top nav — transparent over hero, frosted once scrolled past */}
      <nav
        className={`fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-6 md:px-8 py-5 transition-all duration-500 ${
          navScrolled
            ? isDarkMode
              ? 'bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5'
              : 'bg-white/80 backdrop-blur-md border-b border-black/5'
            : ''
        }`}
      >
        <a
          href="#"
          className={`text-base font-semibold tracking-tight cursor-pointer ${
            isDarkMode ? 'text-white' : 'text-dark'
          }`}
        >
          Solvarion LTD
        </a>
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'About Us', href: '#about' },
            { label: 'Our Services', href: '#features' },
            { label: 'Our Products', href: '#portfolio' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`text-sm font-medium hover:opacity-70 transition-opacity cursor-pointer ${
                isDarkMode ? 'text-white' : 'text-dark'
              }`}
            >
              {item.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsDarkMode((v) => !v)}
            className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
              isDarkMode
                ? 'border-white/30 text-white hover:bg-white/10'
                : 'border-black/20 text-dark hover:bg-black/5'
            }`}
            aria-label="Toggle theme"
          >
            <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-sm`}></i>
          </button>
          <a
            href="/contact"
            className={`hidden md:inline-flex items-center rounded-full px-5 py-2 text-xs font-medium hover:scale-105 transition-transform duration-300 whitespace-nowrap cursor-pointer ${
              isDarkMode
                ? 'bg-white text-black hover:bg-gray-200'
                : 'bg-black text-white hover:bg-gray-900'
            }`}
          >
            Contact Us
          </a>
          <MobileMenu
            isDarkMode={isDarkMode}
            links={[
              { label: 'About Us', href: '#about' },
              { label: 'Our Services', href: '#features' },
              { label: 'Our Products', href: '#portfolio' },
            ]}
            ctaHref="/contact"
            ctaLabel="Contact Us"
          />
        </div>
      </nav>

      {/* Scroll-driven tunnel hero */}
      <section className="relative" style={{ height: '200vh' }}>
        <div className="sticky top-0 left-0 w-full h-screen">
          <Hero isDarkMode={isDarkMode} />
        </div>
      </section>

      <Features isDarkMode={isDarkMode} />
      <Portfolio isDarkMode={isDarkMode} />
      <Pricing isDarkMode={isDarkMode} />
      <About isDarkMode={isDarkMode} />
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}